import dayjs from 'dayjs';

/**
 * Profile Form Mode Constants
 */
export const PROFILE_MODES = {
  CREATE: 'create',
  EDIT: 'edit',
  SKILLKO: 'skillko',
  SITEPASS: 'sitepass',
};

export const isCreateLikeMode = (mode) =>
  mode === PROFILE_MODES.CREATE ||
  mode === PROFILE_MODES.SKILLKO ||
  mode === PROFILE_MODES.SITEPASS;

/**
 * Whether the current user can mutate the profile form.
 * Create / Skillko / SitePass registration uses CREATE_PROFILE.
 * Editing an existing local profile uses EDIT_PROFILE.
 */
export const canWriteProfileForm = ({
  mode,
  isDeletedProfile,
  canEditProfile,
  canCreateProfile,
}) => {
  if (isDeletedProfile) {
    return false;
  }

  if (isCreateLikeMode(mode)) {
    return Boolean(canCreateProfile);
  }

  return Boolean(canEditProfile);
};

/**
 * Fields that are normally kept in sync with Skillko (see `SkillkoComponent.UpdateOnboardedProfileAsync`
 * on the backend). In Edit mode, editing these fields for a profile whose `source` is 'Skillko' is
 * still allowed, but the user should be warned that their change may be overwritten by the next
 * Skillko sync (every 15 minutes) unless they update the data in Skillko itself.
 *
 * Note: certificates are intentionally excluded - certificates are already locked entirely for Skillko-sourced
 * profiles (see CertificatesSection).
 *
 * `picture` is also Skillko-critical (Skillko's `imgurl` is synced onto the profile picture), but
 * unlike the other sections its "changed" state isn't tracked via `profileData` - see
 * `isPictureEdited` / the `picture` special-case in `useProfileForm`'s `requestSaveSection`.
 */
export const SKILLKO_CRITICAL_FIELDS_BY_SECTION = {
  personalDetails: ['firstName', 'surname', 'dateOfBirth', 'phone'],
  nextOfKin: ['nextOfKin', 'nextOfKinPhone'],
  employeeDetails: ['company', 'role'],
  picture: ['image'],
};

export const ALL_SKILLKO_CRITICAL_FIELDS = Object.values(SKILLKO_CRITICAL_FIELDS_BY_SECTION).flat();

/**
 * Whether a given field name is considered Skillko-critical (i.e. normally synced from Skillko).
 */
export const isSkillkoCriticalField = (fieldName) => ALL_SKILLKO_CRITICAL_FIELDS.includes(fieldName);

/**
 * Whether the profile currently being edited is Skillko-sourced and therefore subject to the
 * critical-field warning (only applicable in Edit mode).
 */
export const isSkillkoSourcedEdit = (mode, profileData) =>
  mode === PROFILE_MODES.EDIT && profileData?.source === 'Skillko';

/**
 * Returns the list of critical fields within a section that differ between the initial and
 * current profile data - i.e. the fields that would trigger the Skillko edit warning if saved.
 * @param {string} sectionKey - One of the keys of SKILLKO_CRITICAL_FIELDS_BY_SECTION
 */
export const getChangedCriticalFields = (sectionKey, profileData, initialProfileData) => {
  const fields = SKILLKO_CRITICAL_FIELDS_BY_SECTION[sectionKey] || [];
  return fields.filter((field) => initialProfileData?.[field] !== profileData?.[field]);
};

/**
 * Induction errors where saving the picture should call RetryInductionProfilePicture
 * (same as the optional retry flow for normalisation failures).
 */
export function isRetryablePictureInductionError(message) {
  if (!message) return false;
  return (
    message.startsWith('NormaliseAndSaveProfileFaceFromImage failed') ||
    message === 'ImageFile is null' ||
    message.includes('Failed at step: NormalizeFaceImage')
  );
}

/**
 * Default profile data structure shared across all modes
 */
export const getDefaultProfileData = (mode) => {
  const base = {
    firstName: '',
    surname: '',
    dateOfBirth: null,
    phone: '',
    company: null,
    role: null,
    email: '',
    nextOfKin: '',
    nextOfKinPhone: '',
    validFrom: dayjs(),
    expiresAt: dayjs().add(1, 'year'),
    access: null,
    isAntiPassback: true,
    certificates: [],
    gateAccess: false,
  };

  // Edit mode has additional fields
  if (mode === PROFILE_MODES.EDIT) {
    return {
      ...base,
      status: 'Draft',
      inductionErrorMessage: null,
      source: null,
      skillkoAutomaticExpiry: false,
    };
  }

  // Skillko mode has additional fields
  if (mode === PROFILE_MODES.SKILLKO) {
    return {
      ...base,
      skillkoCertificates: [],
      failedRules: [],
      skillkoAutomaticExpiry: true,
    };
  }

  // SitePass mode has additional fields
  if (mode === PROFILE_MODES.SITEPASS) {
    return {
      ...base,
      sitePassUniqueId: '',
      inductionExpiry: null,
    };
  }

  return base;
};

/**
 * Field configuration for each mode
 * - disabled: whether the field should be disabled (for Skillko, this is dynamic based on API response)
 * - showEdited: whether to show "(Edited)" indicator when value changes (Edit mode only)
 */
const createFieldConfig = () => ({
  firstName: { disabled: false, showEdited: false },
  surname: { disabled: false, showEdited: false },
  email: { disabled: false, showEdited: false },
  dateOfBirth: { disabled: false, showEdited: false },
  phone: { disabled: false, showEdited: false },
  nextOfKin: { disabled: false, showEdited: false },
  nextOfKinPhone: { disabled: false, showEdited: false },
  company: { disabled: false, showEdited: false },
  role: { disabled: false, showEdited: false },
  access: { disabled: false, showEdited: false },
  validFrom: { disabled: false, showEdited: false },
  expiresAt: { disabled: false, showEdited: false },
  isAntiPassback: { disabled: false, showEdited: false },
});

const editFieldConfig = () => ({
  firstName: { disabled: false, showEdited: true },
  surname: { disabled: false, showEdited: true },
  email: { disabled: false, showEdited: false }, // Email not editable in edit mode
  dateOfBirth: { disabled: false, showEdited: true },
  phone: { disabled: false, showEdited: true },
  nextOfKin: { disabled: false, showEdited: true },
  nextOfKinPhone: { disabled: false, showEdited: true },
  company: { disabled: false, showEdited: true },
  role: { disabled: false, showEdited: true },
  access: { disabled: false, showEdited: true },
  validFrom: { disabled: false, showEdited: true },
  expiresAt: { disabled: false, showEdited: true },
  isAntiPassback: { disabled: false, showEdited: true },
});

// Skillko fields are dynamically disabled based on API response
const skillkoFieldConfig = () => ({
  firstName: { disabled: 'dynamic', showEdited: false },
  surname: { disabled: 'dynamic', showEdited: false },
  email: { disabled: 'dynamic', showEdited: false },
  dateOfBirth: { disabled: 'dynamic', showEdited: false },
  phone: { disabled: 'dynamic', showEdited: false },
  nextOfKin: { disabled: 'dynamic', showEdited: false },
  nextOfKinPhone: { disabled: 'dynamic', showEdited: false },
  company: { disabled: 'dynamic', showEdited: false },
  role: { disabled: 'dynamic', showEdited: false },
  access: { disabled: false, showEdited: false },
  validFrom: { disabled: false, showEdited: false },
  expiresAt: { disabled: false, showEdited: false },
  isAntiPassback: { disabled: false, showEdited: false },
});

// SitePass fields are dynamically disabled based on API response
const sitepassFieldConfig = () => ({
  firstName: { disabled: 'dynamic', showEdited: false },
  surname: { disabled: 'dynamic', showEdited: false },
  email: { disabled: 'dynamic', showEdited: false },
  dateOfBirth: { disabled: false, showEdited: false },
  phone: { disabled: 'dynamic', showEdited: false },
  nextOfKin: { disabled: false, showEdited: false },
  nextOfKinPhone: { disabled: false, showEdited: false },
  company: { disabled: false, showEdited: false },
  role: { disabled: false, showEdited: false },
  access: { disabled: false, showEdited: false },
  validFrom: { disabled: false, showEdited: false },
  expiresAt: { disabled: false, showEdited: false },
  isAntiPassback: { disabled: false, showEdited: false },
});

/**
 * Mode configurations
 */
const modeConfigs = {
  [PROFILE_MODES.CREATE]: {
    // Page metadata
    pageTitle: 'profiles.form.pageTitleCreate',
    breadcrumbTitle: 'profiles.form.breadcrumbAddNew',
    newProfileText: 'profiles.form.newProfile',
    
    // Save behavior
    sectionSave: false,
    showDraftButton: true,
    showDeleteButton: false,
    showActivateButton: false,
    showSkillkoLink: false,
    
    // Field configuration
    fields: createFieldConfig(),
    
    // Section configuration
    sections: {
      certificates: { 
        editable: true, 
        showSkillkoCerts: false,
        dividerText: 'Safe pass',
      },
      failedRulesAlert: false,
      showEmail: true,
    },
    
    // Validation rules
    validation: {
      requireImage: true,
      requireEmail: true,
      requireFirstName: true,
      requireSurname: true,
      requireDateOfBirth: true,
      requireCompany: true,
      requireAccess: true,
      requireRole: true,
    },
    
    // API configuration
    api: {
      loadData: null,
      saveProfile: 'createProfile',
      saveDraft: 'saveProfileDraft',
      source: 'UIInduction',
    },

    // Navigation
    navigation: {
      backState: { openOnManageProfiles: true },
    },
  },
  
  [PROFILE_MODES.EDIT]: {
    // Page metadata
    pageTitle: 'profiles.form.pageTitleEdit',
    breadcrumbTitle: 'profiles.form.breadcrumbEdit',
    newProfileText: 'profiles.form.profile',
    
    // Save behavior
    sectionSave: true,
    showDraftButton: false,
    showDeleteButton: true,
    showActivateButton: true, // Only shown when status is 'Draft'
    showSkillkoLink: true, // Only shown when source is 'Skillko'
    showSkillkoRefresh: true, // Only shown when source is 'Skillko'
    
    // Field configuration
    fields: editFieldConfig(),
    
    // Section configuration
    sections: {
      certificates: { 
        editable: true, // Disabled when source is Skillko
        showSkillkoCerts: false,
        dividerText: 'Certificates',
      },
      failedRulesAlert: false,
      showEmail: false, // Email shown in header for edit mode
      showInductionError: true,
    },
    
    // Validation rules
    validation: {
      requireImage: true,
      requireEmail: false, // Email not editable
      requireFirstName: true,
      requireSurname: true,
      requireDateOfBirth: false,
      requireCompany: true,
      requireAccess: true,
      requireRole: true,
    },
    
    // API configuration
    api: {
      loadData: 'getProfileData',
      savePersonalDetails: 'editProfilePersonalDetails',
      saveNextOfKin: 'editProfileNextOfKin',
      saveEmployeeDetails: 'editProfileEmployeeDetails',
      saveAccess: 'editProfileAccess',
      saveCertificates: 'updateProfileCertificates',
      savePicture: 'editProfilePicture',
      retryPicture: 'retryRegistrationProfilePicture',
      deleteProfile: 'deleteProfile',
      activateProfile: 'activateProfileDraft',
      source: null,
    },

    // Navigation
    navigation: {
      backState: { openOnManageProfiles: true },
    },
  },
  
  [PROFILE_MODES.SKILLKO]: {
    // Page metadata
    pageTitle: 'profiles.form.pageTitleRegister',
    breadcrumbTitle: 'profiles.form.breadcrumbRegister',
    newProfileText: 'profiles.form.newProfile',
    
    // Save behavior
    sectionSave: false,
    showDraftButton: false,
    showDeleteButton: false,
    showActivateButton: false,
    showSkillkoLink: true,
    showSkillkoRefresh: true,
    
    // Field configuration (dynamic based on API response)
    fields: skillkoFieldConfig(),
    
    // Section configuration
    sections: {
      certificates: { 
        editable: false, 
        showSkillkoCerts: true,
        dividerText: 'Certificates',
      },
      failedRulesAlert: true,
      showEmail: true,
    },
    
    // Validation rules
    validation: {
      requireImage: true,
      requireEmail: true,
      requireFirstName: true,
      requireSurname: true,
      requireDateOfBirth: true,
      requireCompany: true,
      requireAccess: true,
      requireRole: true,
    },
    
    // API configuration
    api: {
      loadData: 'getProfileFromSkillko',
      saveProfile: 'createProfile',
      source: 'Skillko',
    },

    // Navigation
    navigation: {
      backState: { openOnInductionComplete: true },
    },
  },

  [PROFILE_MODES.SITEPASS]: {
    // Page metadata
    pageTitle: 'profiles.form.pageTitleRegisterSitepass',
    breadcrumbTitle: 'profiles.form.breadcrumbRegisterSitepass',
    newProfileText: 'profiles.form.newSitepassProfile',
    
    // Save behavior
    sectionSave: false,
    showDraftButton: false,
    showDeleteButton: false,
    showActivateButton: false,
    showSkillkoLink: false,
    showSitePassChip: true,
    
    // Field configuration (dynamic based on API response)
    fields: sitepassFieldConfig(),
    
    // Section configuration
    sections: {
      certificates: { 
        editable: true, 
        showSkillkoCerts: false,
        dividerText: 'Certificates',
      },
      failedRulesAlert: false,
      showEmail: true,
      showInductionExpiryWarning: true,
    },
    
    // Validation rules (less strict than Skillko - DOB and role not required)
    validation: {
      requireImage: true,
      requireEmail: true,
      requireFirstName: true,
      requireSurname: true,
      requireDateOfBirth: false,
      requireCompany: false,
      requireAccess: true,
      requireRole: false,
    },
    
    // API configuration
    api: {
      loadData: 'getProfileFromSitePass',
      saveProfile: 'createProfile',
      source: 'SitePass',
    },

    // Navigation
    navigation: {
      backState: { openOnSitePassReady: true },
    },
  },
};

/**
 * Get configuration for a specific mode
 * @param {string} mode - One of PROFILE_MODES values
 * @returns {object} Mode configuration
 */
export const getModeConfig = (mode) => {
  const config = modeConfigs[mode];
  if (!config) {
    throw new Error(`Invalid profile mode: ${mode}`);
  }
  return config;
};

/**
 * Check if a field is disabled based on mode and dynamic disabled fields
 * @param {string} mode - Current mode
 * @param {string} fieldName - Field name to check
 * @param {object} dynamicDisabledFields - Object with field names as keys and boolean values
 * @returns {boolean} Whether the field is disabled
 */
export const isFieldDisabled = (mode, fieldName, dynamicDisabledFields = {}) => {
  const config = getModeConfig(mode);
  const fieldConfig = config.fields[fieldName];
  
  if (!fieldConfig) {
    return false;
  }
  
  if (fieldConfig.disabled === 'dynamic') {
    return !!dynamicDisabledFields[fieldName];
  }
  
  return fieldConfig.disabled;
};

/**
 * Check if field should show "Edited" indicator
 * @param {string} mode - Current mode
 * @param {string} fieldName - Field name to check
 * @returns {boolean} Whether to show edited indicator
 */
export const shouldShowEdited = (mode, fieldName) => {
  const config = getModeConfig(mode);
  const fieldConfig = config.fields[fieldName];
  return fieldConfig?.showEdited ?? false;
};

/**
 * Get validation function based on mode
 */
export const getValidationRules = (mode) => {
  return getModeConfig(mode).validation;
};

